import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert } from 'react-bootstrap';
import { Globe, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { WebSource } from '../../../types';
import { apiService } from '../../../services/apiService';

interface WebsiteSelectionModalProps {
    show: boolean;
    onHide: () => void;
    onSelect: (selectedWebsites: Array<{ id: string; crawlDepth: number }>) => void;
    currentSelected: Array<{ id: string; crawlDepth: number }>;
}

const WebsiteSelectionModal: React.FC<WebsiteSelectionModalProps> = ({
                                                                         show,
                                                                         onHide,
                                                                         onSelect,
                                                                         currentSelected
                                                                     }) => {
    const [webSources, setWebSources] = useState<WebSource[]>([]);
    const [selectedWebsites, setSelectedWebsites] = useState(
        new Map(currentSelected.map(website => [website.id, website.crawlDepth]))
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchWebSources = async () => {
        try {
            setIsLoading(true);
            const data = await apiService.get<{ webSources: WebSource[] }>(API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.LIST);
            setWebSources(data.webSources || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch websites');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchWebSources();
            setSelectedWebsites(new Map(currentSelected.map(website => [website.id, website.crawlDepth])));
        }
    }, [show, currentSelected]);

    const handleWebsiteSelection = (webSource: WebSource) => {
        const sourceId = webSource.id;
        setSelectedWebsites(prev => {
            const newMap = new Map(prev);
            if (newMap.has(sourceId)) {
                newMap.delete(sourceId);
            } else {
                newMap.set(sourceId, webSource.configuration.crawl_depth || 2);
            }
            return newMap;
        });
    };

    const handleSave = () => {
        const selectedSources = webSources
            .filter(source => selectedWebsites.has(source.id))
            .map(source => ({
                id: source.id,
                crawlDepth: selectedWebsites.get(source.id) || 2
            }));
        onSelect(selectedSources);
        onHide();
    };

    const filteredWebSources = webSources.filter(source =>
        source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.configuration.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (source.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Websites</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                <Form.Group className="mb-3">
                    <div className="position-relative">
                        <div className="position-absolute top-50 translate-middle-y ps-3">
                            <Search size={16} className="text-muted" />
                        </div>
                        <Form.Control
                            type="text"
                            placeholder="Search websites..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="ps-5 border"
                            style={{ height: '38px' }}
                        />
                    </div>
                </Form.Group>

                {isLoading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <Table hover responsive>
                        <thead>
                        <tr>
                            <th style={{ width: '30px' }}></th>
                            <th>Name</th>
                            <th>URL</th>
                            <th>Crawl Frequency</th>
                            <th style={{ width: '120px' }}>Crawl Depth</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredWebSources.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center text-muted py-4">
                                    <Globe size={24} className="mb-2" />
                                    <div>No websites found</div>
                                </td>
                            </tr>
                        ) : (
                            filteredWebSources.map((source) => (
                                <tr
                                    key={source.id}
                                    onClick={() => handleWebsiteSelection(source)}
                                    className={selectedWebsites.has(source.id) ? 'table-primary' : ''}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedWebsites.has(source.id)}
                                            onChange={() => handleWebsiteSelection(source)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <Globe size={16} className="me-2 text-muted" />
                                            {source.name}
                                        </div>
                                    </td>
                                    <td className="text-muted small">
                                        {source.configuration.url}
                                    </td>
                                    <td className="text-muted small" style={{whiteSpace: 'nowrap'}}>
                                        {source.configuration.crawl_frequency
                                            ?.split('')
                                            .map((char, i) => i === 0 ? char.toUpperCase() : char)
                                            .join('') ?? '-'}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <span className="text-muted small">
                                            {selectedWebsites.has(source.id)
                                                ? selectedWebsites.get(source.id)
                                                : source.configuration.crawl_depth || 2}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={selectedWebsites.size === 0}
                >
                    Select ({selectedWebsites.size}) {selectedWebsites.size === 1 ? 'Website' : 'Websites'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default WebsiteSelectionModal;